import { useState } from 'react';
import ToggleButton from './ToggleButton';
import HotWaterEquipmentField from './HotWaterEquipmentField';
import EquipmentPhotoSection from './EquipmentPhotoSection';
import RatingLetterPicker from './RatingLetterPicker';
import { useAppMetadata } from '../../shared/useAppMetadata';

function MachineFields({ data, onChange, onChangeCustomField, imagePreview, onImageChange, onDeleteImage, photoDisabled }) {
    const [showMore, setShowMore] = useState(false);
    const { data: machineTypes, loading: loadingMachineTypes } = useAppMetadata('machine_types');

    const handleToggle = (name) => (e) => onChange({ target: { name, value: e.target.checked } });
    const selectedType = machineTypes.find((t) => t.value === data.type_machine);
    const customFields = selectedType?.fields || [];
    const customValues = data.custom_fields || {};

    return (
        <div className="equip-fields">

            {/* Nombre */}
            <div className="equip-field">
                <span className="equip-field-label">Nombre d'équipements</span>
                <input
                    type="number"
                    name="nb_equipment"
                    value={data.nb_equipment ?? ''}
                    onChange={onChange}
                    min={1}
                    className="equip-field-num"
                />
            </div>

            {/* Type de machine */}
            <div className={`equip-field equip-field--col${!data.type_machine ? ' equip-field--required' : ''}`}>
                <span className="equip-field-label">
                    Type de machine
                    <span className="equip-field-required-star"> *</span>
                    {selectedType?.description && (
                        <span className="equip-info-icon" tabIndex={0}>
                            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.3" />
                                <path d="M8 7.2V11.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                <circle cx="8" cy="4.8" r="0.9" fill="currentColor" />
                            </svg>
                            <span className="equip-info-tooltip">{selectedType.description}</span>
                        </span>
                    )}
                </span>
                <select
                    className="equip-select"
                    value={data.type_machine || ''}
                    onChange={(e) => {
                        const value = e.target.value;
                        const newType = machineTypes.find((t) => t.value === value);
                        onChange({ target: { name: 'type_machine', value } });
                        onChange({ target: { name: 'volume_per_cycle_unit', value: newType?.default_volume_unit || 'L/cycle' } });
                    }}
                    disabled={loadingMachineTypes}
                >
                    <option value="">{loadingMachineTypes ? 'Chargement...' : 'Sélectionner un type de machine'}</option>
                    {machineTypes.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                </select>
            </div>

            {/* Référence */}
            <div className="equip-field equip-field--col">
                <span className="equip-field-label">Référence</span>
                <input type="text" name="reference" value={data.reference || ''} onChange={onChange} placeholder="Référence ou modèle" />
            </div>

            {/* Volume par cycle / débit */}
            <div className={`equip-field${data.volume_per_cycle === null || data.volume_per_cycle === undefined ? ' equip-field--required' : ''}`}>
                <span className="equip-field-label">
                    {data.volume_per_cycle_unit === 'L/min' ? 'Débit' : 'Volume par cycle'}
                    <span className="equip-field-required-star"> *</span>
                </span>
                <div className="equip-field-compound">
                    <input
                        type="number"
                        name="volume_per_cycle"
                        value={data.volume_per_cycle ?? ''}
                        onChange={onChange}
                        min={0}
                        className="equip-field-num"
                    />
                    <select
                        name="volume_per_cycle_unit"
                        value={data.volume_per_cycle_unit || 'L/cycle'}
                        onChange={onChange}
                        className="equip-field-unit-select"
                    >
                        <option value="L/cycle">L/cycle</option>
                        <option value="L/min">L/min</option>
                    </select>
                </div>
            </div>

            {/* Champs personnalisés du type de machine sélectionné */}
            {customFields.length > 0 && (
                <div className="equip-custom-fields-box">
                    <span className="equip-custom-fields-title">Caractéristiques {selectedType.label}</span>

                    {customFields.map((field) => {
                        const value = customValues[field.key];
                        const empty = value === null || value === undefined || value === '';
                        const requiredClass = field.required && empty ? ' equip-field--required' : '';

                        if (field.type === 'checkbox') {
                            return (
                                <div key={field.key} className={`equip-toggle-card${value ? ' equip-toggle-card--on' : ''}`}>
                                    <div className="equip-toggle-card-body">
                                        <span className="equip-toggle-card-label">{field.label}</span>
                                    </div>
                                    <ToggleButton
                                        name={field.key}
                                        value={!!value}
                                        onChange={(e) => onChangeCustomField(field.key, e.target.checked)}
                                        context="equipment-custom"
                                    />
                                </div>
                            );
                        }

                        if (field.type === 'number') {
                            return (
                                <div key={field.key} className={`equip-field${requiredClass}`}>
                                    <span className="equip-field-label">
                                        {field.label}
                                        {field.required && <span className="equip-field-required-star"> *</span>}
                                    </span>
                                    <div className="equip-field-compound">
                                        <input
                                            type="number"
                                            value={value ?? ''}
                                            onChange={(e) => onChangeCustomField(field.key, e.target.value === '' ? null : parseFloat(e.target.value))}
                                            className="equip-field-num"
                                        />
                                        {field.unit && <span className="equip-field-unit-badge">{field.unit}</span>}
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={field.key} className={`equip-field equip-field--col${requiredClass}`}>
                                <span className="equip-field-label">
                                    {field.label}
                                    {field.required && <span className="equip-field-required-star"> *</span>}
                                </span>
                                <input
                                    type="text"
                                    value={value ?? ''}
                                    onChange={(e) => onChangeCustomField(field.key, e.target.value)}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Eau chaude : raccordement + part typique héritée par les usages */}
            <HotWaterEquipmentField data={data} equipmentType="machine" onChange={onChange} />

            {/* Photo */}
            <EquipmentPhotoSection imagePreview={imagePreview} onImageChange={onImageChange} onDeleteImage={onDeleteImage} disabled={photoDisabled} />

            {/* Note d'efficacité */}
            <div className="equip-field equip-field--col">
                <span className="equip-field-label">Note d'efficacité</span>
                <RatingLetterPicker value={data.rate} onChange={(rate) => onChange({ target: { name: 'rate', value: rate } })} />
            </div>

            {/* Commentaire */}
            <div className="equip-field equip-field--col">
                <span className="equip-field-label">Commentaire</span>
                <input type="text" name="general_comment" value={data.general_comment || ''} onChange={onChange} placeholder="Commentaire général" />
            </div>

            {/* Autres informations */}
            <button
                type="button"
                className={`equip-more-btn${showMore ? ' equip-more-btn--open' : ''}`}
                onClick={() => setShowMore(p => !p)}
            >
                <span>Autres informations</span>
                <svg className="equip-more-chevron" width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </button>

            {showMore && (
                <div className="equip-more-content">

                    <div className="equip-field">
                        <span className="equip-field-label">Vanne d'isolement</span>
                        <ToggleButton name="isolation_valve" value={!!data.isolation_valve} onChange={handleToggle('isolation_valve')} context="equipment" />
                    </div>

                    <div className="equip-field equip-field--col equip-field--last">
                        <span className="equip-field-label">Qualité de l'eau en entrée</span>
                        <select name="water_quality_in" value={data.water_quality_in || 'drinkable'} onChange={onChange}>
                            <option value="drinkable">Eau potable</option>
                            <option value="non_drinkable">Eau non potable</option>
                        </select>
                    </div>

                </div>
            )}
        </div>
    );
}

export default MachineFields;
